import { useEffect, useMemo } from 'react';
import { useAgentConversationListApi, useAgentTranscriptApi } from '@hero/api-hooks';
import { useAgentChatStore } from './store';
import { readOpenConfirmations } from './readOpenConfirmations';

/**
 * Surface unanswered confirmation cards from an earlier page load.
 *
 * The server-side thread outlives the tab: a card the user never
 * answered is still open when the next message arrives, and the agent
 * is told about it. Without this the panel came up empty after a
 * reload, so the agent would offer to confirm drafts the user could
 * neither see nor answer.
 *
 * Replaying the whole transcript would fix that but costs the empty
 * state — the prompt chips only render on a fresh panel. So we fetch
 * the thread, keep just the open cards, and park them in `resumable`
 * for `ResumeStrip` to offer. Nothing enters the conversation until
 * the user says so.
 */
export const useAgentResumeManager = (input: { enabled: boolean }) => {
  const offerResume = useAgentChatStore(s => s.offerResume);
  const hasBubbles = useAgentChatStore(s => s.bubbles.length > 0);
  const conversationId = useAgentChatStore(s => s.conversationId);

  // Nothing to offer once the panel holds a live thread.
  const wantsResume = input.enabled && !hasBubbles && !conversationId;

  // `skip` / `limit` are owned by the hook layer; the endpoint defaults
  // to newest-first, so the caller's live in-app thread is row 0.
  const conversations = useAgentConversationListApi(
    { channel: 'in_app' },
    { enabled: wantsResume },
  );

  const latestConversationId = conversations.data?.[0]?._id;

  const transcript = useAgentTranscriptApi(
    { conversationId: latestConversationId ?? '' },
    { enabled: wantsResume && !!latestConversationId },
  );

  const openCards = useMemo(
    () => (transcript.data ? readOpenConfirmations(transcript.data) : []),
    [transcript.data],
  );

  useEffect(() => {
    if (!wantsResume || !latestConversationId || openCards.length === 0) return;
    offerResume({
      conversationId: latestConversationId,
      confirmations: openCards,
    });
  }, [wantsResume, latestConversationId, openCards, offerResume, transcript.data]);
};
